/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material'
import {
  CODEBOOK_FILE_NAME,
  CODEBOOK_FILE_PATH,
  METRIC_ENDPOINT_RANK,
  METRIC_ENDPOINT_WEIGHT,
  RANKED_COLOR,
} from 'constant'
import { useAppSelector } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import {
  calculateWeightedGlobalBarChart,
  createMetricData,
  generateGlobalBarChartData,
  isAllMetricValueEmpty,
  isAllPillarValueEmpty,
  normalizeDisabledPillars,
} from 'utils'
import { CustomizedDot } from './utils'
import { MetricGroup } from 'types'

export interface SlopeChartReportProps {
  name: string
}

const SlopeChartReport = ({ name }: SlopeChartReportProps) => {
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
  const [metricData, setMetricData] = useState<MetricGroup[]>([])
  const [modifiedMetricGroupsCache, setModifiedMetricGroupsCache] = useState<Record<number, any>>({})
  const [renderChartDataCache, setRenderChartDataCache] = useState<Record<number, any>>({})
  const [globalBarChartDataCache, setGlobalBarChartDataCache] = useState<Record<number, any>>({})

  // Past logic - 1
  // const renderChartData = calculateWeightedGlobalBarChart(
  //   metricGroups,
  //   configuration.metricValue,
  //   countryData,
  //   configuration.yearFilter.currentYear,
  // )
  // const slopeData = useMemo<Country[]>(() => {
  //   return calculateCountryDataFromMetricValue(metricGroups, renderChartData, {
  //     globalYear: configuration.yearFilter.currentYear,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricValue: configuration.metricValue,
  //   })
  // }, [renderChartData.length, metricGroups.length, renderChartTimes])

  // const data = useMemo<SlopeData>(() => {
  //   if (!countryCode) return []
  //   return generateSlopeChartData(metricGroups, slopeData, {
  //     yearRange: configuration.yearFilter.yearRange,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricValue: configuration.metricValue,
  //     sort: 'asc',
  //     countryCode: countryCode,
  //   })
  // }, [slopeData.length, metricGroups.length, renderChartTimes])

  // Past logic - 2
  // const data = useMemo(() => {
  //   if (metricGroups.length === 0 || countryData.length === 0 || configuration.yearFilter.yearRange.length === 0) {
  //     return []
  //   }
  //   let list: any = []
  //   try {
  //     list = [...configuration?.yearFilter?.yearRange]
  //       ?.sort((a, b) => a - b)
  //       ?.map((year) => {
  //         const modifiedMetricGroups = reCalculateMetricGroupsForAYear(
  //           metricGroups,
  //           countryData,
  //           configuration.metricValue,
  //           year,
  //         )
  //         const renderChartDataBar = calculateWeightedGlobalBarChart(
  //           modifiedMetricGroups,
  //           configuration.metricValue,
  //           countryData,
  //           year,
  //         )
  //         console.log(year, { renderChartDataBar })
  //         const dataReturn = generateGlobalBarChartData(modifiedMetricGroups, renderChartDataBar, {
  //           globalYear: year,
  //           metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //           metricEndpointRank: METRIC_ENDPOINT_RANK,
  //           metricValue: configuration.metricValue,
  //         })
  //         console.log(year, { dataReturn })
  //         const rankObject: Record<string, string | number> = {
  //           year: year?.toString(),
  //         }
  //         dataReturn?.forEach((el, index) => {
  //           rankObject[el.countryCode] = index + 1
  //         })
  //         return rankObject
  //       })
  //       .map((el) => {
  //         return {
  //           year: el.year,
  //           // 'United States': el['United States'],
  //           [countryCode]: el[countryCode],
  //         }
  //       })
  //   } catch (error) {
  //     console.log(error)
  //   }
  //   return list
  // }, [countryData, metricGroups, renderChartTimes, configuration.metricValue, countryCode])

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await createMetricData(`${CODEBOOK_FILE_PATH}${CODEBOOK_FILE_NAME}.csv`)
        setMetricData(data) // ✅ store resolved data
      } catch (error) {
        console.error(error)
      }
    }
    fetchMetrics()
  }, [])

  // Step 1: Calculate modified metric groups for each year
  useEffect(() => {
    if (metricData.length === 0 || countryData.length === 0 || configuration.yearFilter.yearRange.length === 0) {
      return
    }

    const newCache: Record<number, any> = {}
    configuration.yearFilter.yearRange.forEach((year) => {
      try {
        const modifiedMetricGroups = normalizeDisabledPillars(countryData, metricData, year, configuration.metricValue)
        newCache[year] = modifiedMetricGroups
      } catch (error) {
        console.log(`Error calculating modified metric groups for year ${year}:`, error)
      }
    })
    setModifiedMetricGroupsCache(newCache)
  }, [metricData, countryData, configuration.metricValue, configuration.yearFilter.yearRange, renderChartTimes])

  // Step 2: Calculate weighted global bar chart data using cached modified metric groups
  useEffect(() => {
    if (Object.keys(modifiedMetricGroupsCache).length === 0) return

    const newCache: Record<number, any> = {}
    configuration.yearFilter.yearRange.forEach((year) => {
      try {
        const modifiedMetricGroups = modifiedMetricGroupsCache[year]
        if (modifiedMetricGroups) {
          const renderChartDataBar = calculateWeightedGlobalBarChart(
            // sampleMetricGroups as any,
            modifiedMetricGroups,
            configuration.metricValue,
            countryData,
            year,
          )
          newCache[year] = renderChartDataBar
        }
      } catch (error) {
        console.log(`Error calculating weighted global bar chart for year ${year}:`, error)
      }
    })
    setRenderChartDataCache(newCache)
  }, [modifiedMetricGroupsCache, configuration.metricValue, countryData, configuration.yearFilter.yearRange])

  // Step 3: Generate global bar chart data using cached render chart data
  useEffect(() => {
    if (Object.keys(renderChartDataCache).length === 0 || Object.keys(modifiedMetricGroupsCache).length === 0) return

    const newCache: Record<number, any> = {}
    configuration.yearFilter.yearRange.forEach((year) => {
      try {
        const modifiedMetricGroups = modifiedMetricGroupsCache[year]
        const renderChartDataBar = renderChartDataCache[year]

        if (modifiedMetricGroups && renderChartDataBar) {
          const dataReturn = generateGlobalBarChartData(modifiedMetricGroups, renderChartDataBar, {
            globalYear: year,
            metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
            metricEndpointRank: METRIC_ENDPOINT_RANK,
            metricValue: configuration.metricValue,
          })
          newCache[year] = dataReturn
        }
      } catch (error) {
        console.log(`Error generating global bar chart data for year ${year}:`, error)
      }
    })
    setGlobalBarChartDataCache(newCache)
  }, [renderChartDataCache, modifiedMetricGroupsCache, configuration.metricValue, configuration.yearFilter.yearRange])

  // Final data calculation using cached global bar chart data
  const data = useMemo(() => {
    if (Object.keys(globalBarChartDataCache).length === 0) return []

    try {
      const list = [...configuration?.yearFilter?.yearRange]
        ?.sort((a, b) => a - b)
        ?.map((year) => {
          const dataReturn = globalBarChartDataCache[year]
          if (!dataReturn) return null

          const rankObject: Record<string, string | number> = {
            year: year?.toString(),
          }
          dataReturn?.forEach((el: any, index: number) => {
            rankObject[el.countryName] = index + 1
          })
          return rankObject
        })
        .filter(Boolean)
        .map((el: any) => {
          return {
            year: el.year,
            [name]: el[name || ''],
          }
        })

      return list
    } catch (error) {
      console.log('Error in final data calculation:', error)
      return []
    }
  }, [globalBarChartDataCache, configuration.yearFilter.yearRange, name])

  const countryTicks = useMemo<{ countryName: string; rank: number; color: string }[]>(() => {
    if (!data || data.length === 0) return []
    return Object.entries(data[0])
      .filter(([key]) => key !== 'year')
      .map(([key, value]) => ({ countryName: key, rank: value as number }))
      .map((el, index) => ({ ...el, color: RANKED_COLOR[index % 5] }))
  }, [data])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <span style={{ fontFamily: 'CircularStdMedium' }}>Ranking Over Time</span>
      <Box
        sx={{
          height: configuration.yearFilter.yearRange.length * 60,
          width: '100%',
          mr: '60px',
          mt: '4px',
        }}
      >
        {!isAllPillarValueEmpty(metricGroups) && !isAllMetricValueEmpty(metricGroups, configuration.metricValue) && (
          <ResponsiveContainer height='100%'>
            <LineChart
              data={data}
              margin={{
                top: 40,
                right: 30,
                left: 20,
                bottom: 25,
              }}
            >
              <defs>
                <filter id={`lineShadow`} x='-50%' y='-50%' width='200%' height='200%' filterUnits='userSpaceOnUse'>
                  <feOffset result='offOut' in='SourceGraphic' dx='5' dy='5' />
                  <feColorMatrix
                    result='matrixOut'
                    in='offOut'
                    type='matrix'
                    values='0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.5 0'
                  />
                  <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='3' />
                  <feBlend in='SourceGraphic' in2='blurOut' mode='normal' />
                </filter>
              </defs>
              <XAxis
                tick={{ dy: 20, fill: 'black' }}
                type='category'
                dataKey='year'
                axisLine={false}
                tickLine={false}
                allowDuplicatedCategory={false}
                interval='preserveStartEnd'
                style={{ fontSize: '11px' }}
              />
              <YAxis
                type='number'
                reversed={true}
                domain={[1, 'dataMax']}
                axisLine={false}
                tickLine={false}
                tick={false}
              />
              <CartesianGrid horizontal={false} />
              {countryTicks.map((countryTick) => {
                return (
                  <Line
                    key={countryTick.countryName}
                    dataKey={countryTick.countryName}
                    stroke={countryTick.color}
                    dot={<CustomizedDot value={countryTick.rank} boxWidth={40} boxHeight={40} />}
                    isAnimationActive={false}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  )
}

export default SlopeChartReport
